import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, AlertTriangle, AlertCircle, Info, ChevronRight } from "lucide-react";
import { useNotificationSummary } from "../hooks/useNotificationSummary";
import { badgeText } from "../lib/nav";
import { itemDetail, itemTitle } from "../lib/notificationText";

const severityStyles = {
  critical: { icon: AlertCircle, color: "#b91c1c", bg: "#fef2f2" },
  warning: { icon: AlertTriangle, color: "#b45309", bg: "#fffbeb" },
  info: { icon: Info, color: "#0369a1", bg: "#eff6ff" },
};

/**
 * The bell in the top bar. It reads the layout's one poll of
 * /api/notifications/summary (see useNotificationSummary) and opens a list
 * of what is waiting, each row a link to the page that resolves it.
 */
const NotificationsBell = ({ onDark = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary } = useNotificationSummary();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const goTo = (link) => {
    setOpen(false);
    navigate(link);
  };

  const label = summary.total > 0
    ? t("notifications.buttonLabelCount", { count: summary.total })
    : t("notifications.title");

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        title={t("notifications.title")}
        className={`relative inline-flex items-center justify-center w-11 h-11 md:w-10 md:h-10 rounded-lg transition-colors ${
          onDark
            ? "text-slate-300 hover:text-white hover:bg-slate-800"
            : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        <Bell className="w-5 h-5" />
        {summary.total > 0 && (
          <span
            className={`absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1 ring-2 ${onDark ? "ring-[#0f172a]" : "ring-white dark:ring-slate-900"}`}
            style={{ background: summary.has_critical ? "#dc2626" : "#0e7490" }}
            aria-hidden="true"
          >
            {badgeText(summary.total)}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed md:absolute left-2 right-2 md:left-auto md:right-0 top-[60px] md:top-full md:mt-2 md:w-96 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border z-50 overflow-hidden"
          style={{ borderColor: "var(--c-border)" }}
          role="dialog"
          aria-label={t("notifications.title")}
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border-soft)" }}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{t("notifications.title")}</h3>
            {summary.total > 0 && (
              <span className="text-xs text-slate-500 dark:text-slate-400">{t("notifications.itemsCount", { count: summary.total })}</span>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {summary.items.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-6 h-6 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("notifications.empty")}</p>
              </div>
            ) : (
              summary.items.map((item) => {
                const cfg = severityStyles[item.severity] || severityStyles.info;
                const Icon = cfg.icon;
                const detail = itemDetail(t, item);
                return (
                  <button
                    type="button"
                    key={item.type}
                    onClick={() => goTo(item.link)}
                    className="w-full min-h-[56px] px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 text-left flex items-center gap-3 border-b last:border-b-0 transition-colors"
                    style={{ borderColor: "var(--c-border-soft)" }}
                    data-testid={`notification-${item.type}`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cfg.bg }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{itemTitle(t, item)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {item.count}{detail ? ` · ${detail}` : ""}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
                  </button>
                );
              })
            )}
          </div>
          {summary.items.length > 0 && (
            <div className="px-4 py-2 border-t" style={{ borderColor: "var(--c-border-soft)" }}>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">{t("notifications.refreshHint")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
